<template>
    <div class="dd-option-list">
        <v-text-field
            v-if="items.length > searchFrom"
            v-model="query"
            class="search-field dd-option-list__search"
            :label="$t('labels.search')"
            prepend-inner-icon="search"
            background-color="white"
            hide-details
            outlined
            dense
        ></v-text-field>
        <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <div class="dd-option-list__items" role="group">
            <button
                v-for="item in shownItems"
                :key="item[itemValue]"
                type="button"
                class="dd-option"
                :role="multiple ? 'menuitemcheckbox' : 'menuitemradio'"
                :aria-checked="String(isSelected(item))"
                :disabled="disabled"
                @click="toggle(item)"
            >
                <v-icon v-if="multiple" class="dd-option__box">
                    {{
                        isSelected(item)
                            ? 'check_box'
                            : 'check_box_outline_blank'
                    }}
                </v-icon>
                <span
                    v-else
                    :class="[
                        'dd-option__radio',
                        { 'dd-option__radio--on': isSelected(item) },
                    ]"
                ></span>
                <span class="dd-option__label">{{ item.label }}</span>
            </button>
            <div
                v-if="!loading && !matchingItems.length"
                class="dd-option-list__note"
            >
                {{ $t('labels.no_data_available') }}
            </div>
            <div
                v-if="matchingItems.length > shownItems.length"
                class="dd-option-list__note"
            >
                +{{ matchingItems.length - shownItems.length }}
            </div>
        </div>
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import selectapiType from '@/store/type/selectapiType'

// The options of a select filter as the checkbox list of a DoorDash filter pill menu
// (merchant portal Video library: "Status" → Approved / In review / …), tasks#211.
// Loads the same select API list as EntitySelectInput, or shows fixed `options` ({ key, label })
// without loading (the group order restaurant finder, tasks#222); long lists get a search field and
// show the first matches only.
export default {
    props: {
        // a short fixed list (a meal filter) keeps its order, the selected item does not jump first
        keepOrder: Boolean,
        value: {
            type: [Array, String, Number, Boolean],
            default: null,
        },
        entity: {
            type: String,
            default: null,
        },
        options: {
            type: Array,
            default: null,
        },
        translate: Boolean,
        multiple: Boolean,
        useIdField: Boolean,
        useNameField: Boolean,
        queryParams: {
            type: Object,
            default: null,
        },
        disabled: Boolean,
        // narrows / relabels the loaded options (the order payment method filter, tasks#211)
        itemsFilter: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            items: [],
            loading: false,
            query: '',
            searchFrom: 8,
            limit: 100,
        }
    },
    computed: {
        itemValue() {
            return this.useIdField ? 'id' : this.useNameField ? 'name' : 'key'
        },
        selected() {
            if (this.value === null || this.value === undefined) return []
            return Array.isArray(this.value) ? this.value : [this.value]
        },
        matchingItems() {
            const query = this.query.trim().toLowerCase()
            if (!query) return this.items
            return this.items.filter((item) =>
                String(item.label).toLowerCase().includes(query)
            )
        },
        // the selected options first, so an applied value is visible without searching
        shownItems() {
            if (this.keepOrder) return this.matchingItems.slice(0, this.limit)
            const selected = this.matchingItems.filter(this.isSelected)
            const rest = this.matchingItems.filter(
                (item) => !this.isSelected(item)
            )
            return [...selected, ...rest].slice(0, this.limit)
        },
    },
    watch: {
        entity: 'load',
        queryParams: 'load',
        options: 'load',
    },
    created() {
        this.load()
    },
    methods: {
        load() {
            if (this.options) {
                this.items = cloneDeep(this.options)
                return
            }
            if (!this.entity) return
            this.loading = true
            this.$store
                .dispatch(
                    selectapiType.getActionName(
                        this.entity,
                        selectapiType.actions.LOAD
                    ),
                    { query: this.queryParams }
                )
                .then((data) => {
                    const items = cloneDeep(data || [])
                    if (this.translate)
                        items.forEach((item) => {
                            item.label = this.$t(item.label)
                        })
                    this.items = this.itemsFilter
                        ? this.itemsFilter(items)
                        : items
                })
                .finally(() => {
                    this.loading = false
                })
        },
        isSelected(item) {
            // the URL brings ids back as strings
            return this.selected.some((v) => v == item[this.itemValue])
        },
        toggle(item) {
            const key = item[this.itemValue]
            if (this.multiple) {
                const value = this.isSelected(item)
                    ? this.selected.filter((v) => v != key)
                    : [...this.selected, key]
                this.$emit('input', value.length ? value : null)
            } else {
                this.$emit('input', this.isSelected(item) ? null : key)
            }
        },
    },
}
</script>
